import { GroupSecurity } from '@quromedical/fhir-common';
import type { CareTeam } from '@quromedical/models';
import { CareTeamForm, InitialCareTeam } from 'core/care-team';
import {
  Alert,
  Section,
  Skeleton,
  SkeletonProvider,
  Snackbar,
} from 'design-system';
import { useLocalSearchParams } from 'expo-router';
import { useRevalidation } from 'hooks/useRevalidation';
import { useUserSession } from 'hooks/useUserSession';
import { organizationFetcher } from 'integration';
import { CareTeamApi } from 'integration/aggregate';
import type { CareTeamStackScreenProps } from 'navigation';
import type React from 'react';
import { useCallback, useState } from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import { strings } from 'strings';
import useSWR from 'swr';

const api = new CareTeamApi();

type RouteParams = {
  id: string;
};

export const CareTeamScreen: React.FC<
  CareTeamStackScreenProps<'View'>
> = () => {
  const [hasError, setHasError] = useState(false);

  const id = useLocalSearchParams<RouteParams>().id;

  const fetcher = useCallback(() => api.getCareTeam(id), [id]);
  const swrKey = `careTeam/${id}`;

  const { data, isValidating } = useSWR(swrKey, fetcher);

  const userSession = useUserSession();

  const canEdit = userSession.hasAny([GroupSecurity.CareTeamUpdate]);

  const orgFetcher = organizationFetcher();

  const revalidate = useRevalidation(id);

  const onAlertClose = useCallback(() => {
    setHasError(false);
  }, []);

  const loading = isValidating || !data;

  const initial = InitialCareTeam(data as CareTeam);

  return (
    <ScrollView>
      <SkeletonProvider loading={loading}>
        <Section hasBottomMargin hasTopMargin>
          <Skeleton>
            <CareTeamForm
              initial={initial}
              organizationFetcher={orgFetcher}
              canEdit={canEdit}
              onSubmit={revalidate}
            />
          </Skeleton>
          <Snackbar isOpen={hasError} onClose={onAlertClose}>
            <Alert
              actionIcon="close"
              backgroundColor="status-critical"
              title={strings.AlertError}
              onAction={onAlertClose}
              body={strings.CareTeamError}
            />
          </Snackbar>
        </Section>
      </SkeletonProvider>
    </ScrollView>
  );
};
